/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type { FeatureAppDefinition, FeatureAppEnvironment } from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import { InViewContextProvider } from '@oneaudi/feature-app-utils';

import type {
  AudiFootnoteReferenceServiceInterfaceV3,
  AudiFootnoteRefernceServiceScopeManagerInterfaceV3,
} from './feature-service-types';
import type {
  AppContent,
  EditorialTextContent,
  FeatureServiceDependencies,
} from '../../@types/interfaces';
import { getDesignLibrary } from '../utils/alphaHelper';

import FeatureApp from './FeatureApp';

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'locale-service': '^1.0.0',
      'dbad:audi-vue-formatter-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '*', unifiedWebAlphaCommonVersion: '*' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'audi-tracking-service': '^2.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
      'gfa:dealer-context-service': '^1.0.0',
    },
  },

  create: ({
    featureServices,
    config,
  }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');
    const contentService = featureServices['audi-content-service'];
    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];
    const renderModeService = featureServices['audi-render-mode-service'];
    const localeService = featureServices['locale-service'];

    // SSR
    // --------------------------------------------------------------------
    const asyncSSRManager = featureServices['s2:async-ssr-manager'];
    const serializedStateManager = featureServices['s2:serialized-state-manager'];
    // on the server
    if (asyncSSRManager) {
      asyncSSRManager.scheduleRerender(
        (async () => {
          const content = (await contentService?.getContent()) as AppContent;
          serializedStateManager?.register(() => JSON.stringify(content));
        })(),
      );
    }

    const providerfootnoteService = featureServices[
      'audi-footnote-reference-service'
    ] as AudiFootnoteRefernceServiceScopeManagerInterfaceV3;

    const footnoteReferenceService: AudiFootnoteReferenceServiceInterfaceV3 =
      providerfootnoteService?.getDefaultScopeRefService();

    const dealerContextService = featureServices['gfa:dealer-context-service'];

    return {
      render: () => (
        <UniversalEditorProvider
          contentService={contentService!}
          renderModeService={renderModeService}
        >
          <InViewContextProvider logger={loggerService}>
            <div data-fefa-custom-id={footnoteReferenceService?.getConsumerId()}>
              <FeatureApp
                contentService={contentService!}
                trackingService={featureServices['audi-tracking-service']}
                footnoteReferenceService={footnoteReferenceService}
                vueFormatterService={vueFormatterService!}
                localeService={localeService}
                logger={loggerService}
                config={config as unknown as EditorialTextContent}
                dealerContextService={dealerContextService}
              />
            </div>
          </InViewContextProvider>
        </UniversalEditorProvider>
      ),
    };
  },
};

export default featureAppDefinition;
